import { ArrowLeft, Bell } from 'react-feather';
import { Redirect, useHistory } from 'react-router-dom';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AddOrderStatus } from '__generated__/graphql';

import { CheckoutProps } from './CheckoutProvider';
import { CheckoutStep } from './helpers';
import { EmbedContext } from '../Common/EmbedProvider';
import { HeaderContainer } from '../Common/StickyHeader';
import { SectionTitle, TitleBar } from '../Common/Layout';
import CheckoutTimers, { CheckoutTimersContainer } from './CheckoutTimers';
import DetailsForm from './Details/DetailsForm';
import ExtrasForm from './Extras/ExtrasForm';
import FundraisingForm from './Fundraising/FundraisingForm';
import InvitationCodeForm from './InvitationCodeForm';
import Pager from './Pager';
import PaymentForm from './Payment/PaymentForm';
import PersonalisationForm from './Personalisation/PersonalisationForm';
import Queue from './Queue';
import TeamForm from './Team/TeamForm';
import TicketsForm from './Tickets/TicketsForm';
import UI from '../../common/UI';
import useAutoScrollToSection from '../Common/useAutoScrollToSection';
import useCheckoutUrlState from './useCheckoutUrlState';
import useDocumentTitle from '../../common/useDocumentTitle';
import useLocale from '../../common/useLocale';

const CheckoutPage = ({
  event,
  ticketCategories,
  checkoutSteps,
  activeStepIndex,
  prevRegistration,
  nextRegistration,
  shouldVisitPreviousRegistration,
  valid,
  completeCheckout,
  loading,
  error,
  status,
  preselectedCouponCode,
  preselectedCharity,
  queue,
  hideTimers,
}: CheckoutProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { scrollToCheckoutForm } = useContext(EmbedContext);
  const { parseDate, formatDate } = useLocale();

  const activeStepName = checkoutSteps[activeStepIndex];

  useDocumentTitle([t(`checkout_steps.${activeStepName}`), event.title, t('common:atleta')]);

  useAutoScrollToSection();

  const [{ paymentStatus }] = useCheckoutUrlState();

  const handleSubmit = () => {
    if (nextRegistration()) {
      scrollToCheckoutForm();
    } else if (activeStepIndex + 1 < checkoutSteps.length && valid[activeStepIndex]) {
      history.push(`/${event.id}/${checkoutSteps[activeStepIndex + 1]}`);
    } else if (valid[activeStepIndex]) {
      completeCheckout().catch(() => scrollToCheckoutForm());
    }

    return true;
  };

  useEffect(() => {
    if (activeStepName !== CheckoutStep.Tickets) {
      scrollToCheckoutForm();
    }
  }, [activeStepName]);

  useEffect(() => {
    // Scroll up to the error message
    if (status === AddOrderStatus.START_PAYMENT_FAILED) {
      scrollToCheckoutForm();
    }
  }, [status, scrollToCheckoutForm]);

  const invitationCode = event.invitation_code;
  const intro = invitationCode?.invitation.description || event.checkout_description;

  const ticketsForSaleCount = ticketCategories.reduce(
    (sum, ticketCategory) => sum + ticketCategory.tickets_for_sale.length,
    0,
  );

  if (activeStepIndex === -1) {
    return <Redirect to={`/${event.id}`} />;
  }

  if (activeStepIndex > 0 && !valid[activeStepIndex - 1]) {
    return <Redirect to={`/${event.id}/${checkoutSteps[activeStepIndex - 1]}`} />;
  }

  return (
    <>
      {intro && (
        <UI.HTML html={intro} />
      )}

      {paymentStatus && (
        <UI.Div>
          {paymentStatus === 'pending' && (
            <UI.Warning>
              {t('common:payments.payment_pending')}
            </UI.Warning>
          )}
          {paymentStatus !== 'pending' && (
            <UI.Error>
              {t('common:payments.payment_failed')}
            </UI.Error>
          )}
        </UI.Div>
      )}

      {queue && (
        <Queue queue={queue} />
      )}

      {!queue && (
        <>
          <UI.Form id="CheckoutForm" name="checkout" onSubmit={() => handleSubmit()}>
            <TitleBar>
              <UI.Div>
                {activeStepIndex > 0 && (
                  <UI.FadeIn>
                    <UI.Link
                      to={shouldVisitPreviousRegistration
                        ? `/${event.id}/${CheckoutStep.Personalisation}?section=EventTitle`
                        : `/${event.id}/${checkoutSteps[activeStepIndex - 1]}?section=EventTitle`}
                      title={t('back')}
                      onClick={() => prevRegistration()}
                      resetScrollPosition={false}
                    >
                      <UI.Icon>
                        <ArrowLeft />
                      </UI.Icon>
                    </UI.Link>
                  </UI.FadeIn>
                )}
              </UI.Div>
              <UI.Div style={{ minWidth: 0 }}>
                <UI.Truncate>
                  {t(`checkout_steps.${activeStepName}`)}
                </UI.Truncate>
              </UI.Div>
              {ticketsForSaleCount > 0 && (
                <UI.Div sc={{ small: true }}>
                  {activeStepIndex + 1}
                  {' / '}
                  {checkoutSteps.length}
                </UI.Div>
              )}
            </TitleBar>

            <CheckoutCard id="CheckoutContainer" sc={{ hasPanels: true, active: true }}>
              {ticketsForSaleCount > 0 && (
                <>
                  {error && (
                    <UI.Div>
                      <UI.ServerErrors error={error} attributes={{ 'team.title': t('common:team_picker.team_name') }} />
                    </UI.Div>
                  )}

                  {status === AddOrderStatus.START_PAYMENT_FAILED && (
                    <UI.Div>
                      <UI.Error>
                        <UI.Strong sc={{ block: true }}>
                          {t('common:payments.could_not_start_payment')}
                        </UI.Strong>
                        {t('common:payments.try_again_in_a_minute')}
                      </UI.Error>
                    </UI.Div>
                  )}

                  {!hideTimers && <CheckoutTimers isSticky={activeStepName !== CheckoutStep.Personalisation} />}

                  {activeStepName === CheckoutStep.Tickets && (
                    <UI.GridContainer style={!hideTimers ? { paddingTop: 0, borderTop: 0 } : {}}>
                      {hideTimers && (
                        <SectionTitle>
                          {t('checkout_timers.choose_your_tickets')}
                        </SectionTitle>
                      )}
                      <TicketsForm
                        preselectedCharity={preselectedCharity}
                        preselectedCouponCode={preselectedCouponCode}
                      />
                    </UI.GridContainer>
                  )}
                  {activeStepName === CheckoutStep.Personalisation && (
                    <PersonalisationForm />
                  )}
                  {activeStepName === CheckoutStep.Extras && (
                    <ExtrasForm />
                  )}
                  {activeStepName === CheckoutStep.Team && (
                    <TeamForm />
                  )}
                  {activeStepName === CheckoutStep.Fundraising && (
                    <FundraisingForm />
                  )}
                  {activeStepName === CheckoutStep.Details && (
                    <DetailsForm />
                  )}
                  {activeStepName === CheckoutStep.Payment && (
                    <PaymentForm />
                  )}

                  <Pager
                    checkoutSteps={checkoutSteps}
                    activeStepIndex={activeStepIndex}
                    loading={loading}
                    key={activeStepName}
                  />
                </>
              )}

              {ticketsForSaleCount === 0 && (
                <UI.FadeIn sc={{ textAlign: 'center' }}>
                  <UI.Legend>
                    {event.registration_opens_at ? (
                      <>
                        {t('event_info.registration_opens_at', {
                          time: formatDate(
                            parseDate(event.registration_opens_at, { timezone: 'UTC' }),
                            // Assumes the timestamp is only present if registration opens in < 1 hour.
                            { format: 'display_time', timezone: null },
                          ),
                        })}
                      </>
                    ) : t('no_tickets_available')}
                  </UI.Legend>
                  {event.enable_interest_list && (
                    <UI.Div sc={{ mt: 4 }}>
                      <UI.LinkButton
                        to={`/${event.id}/interest`}
                        sc={{ brand: 'secondary' }}
                        resetScrollPosition={false}
                      >
                        <UI.Icon>
                          <Bell />
                        </UI.Icon>
                        {' '}
                        {t('interest_list.newsletter')}
                      </UI.LinkButton>
                    </UI.Div>
                  )}
                </UI.FadeIn>
              )}
            </CheckoutCard>
          </UI.Form>

          {event.has_invitation_codes && (
            <InvitationCodeForm />
          )}
        </>
      )}
    </>
  );
};

const CheckoutCard = styled(UI.Card)`
  ${CheckoutTimersContainer} + * {
    border-top: 0 !important;
  }

  ${CheckoutTimersContainer} + ${UI.Div} ${HeaderContainer} {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }
`;

export default CheckoutPage;
